import React, {
  ChangeEvent,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  Button,
  Card,
  Icon,
  Popover,
  PopoverList,
  Switch,
  Text,
  WithPopover,
} from '@lightspeed/design-system-react'
import { T, UT } from '@transifex/react'
import { ApiCustomer, ApiReceiptSettings } from '@vend/types'
import { isAmountEligibleForLSPayLink } from '@vend/business-components'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  selectCustomer,
  selectIsSavedService,
  selectSaleStatus,
  updateReceiptValues,
  selectCurrentSale,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { getOutstandingBalance, isQuote } from '../../../functions/sale'
import {
  usePaymentManager,
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../ng-compat/hooks'
import { RootState } from '../../../state/store'
import { posStateManager } from '../../../ng-compat/managers'
import { getFeatureFromCache } from '../../../util/feature-cache'
import { SaleStatus } from '../../../models/sale.constants'
import { ReceiptTemplateList, SelectReceiptParams } from './ReceiptTemplateList'
import { useReceipts } from './useReceipts'

const LABEL_CONTEXT = 'Label for send email receipt toggle'

const getLabel = ({
  status,
  isSavedService,
  customer,
  withPayLink,
}: {
  status?: string
  isSavedService?: boolean
  customer: ApiCustomer | null
  withPayLink?: boolean
}) => {
  if (!customer) {
    return ''
  }

  const email = customer.email

  if (isQuote(status)) {
    return (
      <Text>
        <UT
          _str="Email quote to <b>{email}</b>"
          _context={LABEL_CONTEXT}
          email={email}
        />
      </Text>
    )
  }

  if (isSavedService) {
    const str = withPayLink
      ? 'Email an updated receipt with pay link to <b>{email}</b>'
      : 'Email an updated receipt to <b>{email}</b>'
    return (
      <Text>
        <UT _str={str} _context={LABEL_CONTEXT} email={email} />
      </Text>
    )
  }

  const str = withPayLink
    ? 'Email a receipt with pay link to <b>{email}</b>'
    : 'Email a receipt to <b>{email}</b>'
  return (
    <Text>
      <UT _str={str} _context={LABEL_CONTEXT} email={email} />
    </Text>
  )
}

interface SelectReceiptButtonProps {
  selected: ApiReceiptSettings
  onSelect: (selected: ApiReceiptSettings) => void
}

const selectShouldEmailReceipt = (state: RootState) =>
  Boolean(state.currentSale.receiptEmailAddress)

const SelectReceiptButton: FunctionComponent<SelectReceiptButtonProps> = ({
  selected,
  onSelect,
}) => {
  const { defaultReceipt, otherReceipts } = useReceipts()
  const [open, setOpen] = useState(false)
  const selectReceipt = (params: SelectReceiptParams) => {
    setOpen(false)
    if (params.type === 'default') {
      onSelect(defaultReceipt || otherReceipts[0])
    } else {
      const selected = otherReceipts.find(
        receipt => receipt.id === params.receiptTemplateId
      )
      if (!selected) {
        return
      }
      onSelect(selected)
    }
  }

  return (
    <WithPopover>
      <Button onClick={() => setOpen(true)} link className="vd-link">
        {selected.name} <Icon icon="fa-caret-down" />
      </Button>
      {open ? (
        <Popover onClose={() => setOpen(false)}>
          <PopoverList>
            <ReceiptTemplateList selectReceipt={selectReceipt} />
          </PopoverList>
        </Popover>
      ) : null}
    </WithPopover>
  )
}

export const EmailReceiptToggle: FunctionComponent = () => {
  const retailerSettingsManager = useRetailerSettingsManager()
  const registerManager = useRegisterManager()
  const register = registerManager.getActiveRegister()
  const paymentManager = usePaymentManager()
  const status = useAppSelector(selectSaleStatus)
  const isSavedService = useAppSelector(selectIsSavedService)
  const customer = useAppSelector(selectCustomer)
  const currentSale = useAppSelector(selectCurrentSale)
  const { defaultReceipt, otherReceipts } = useReceipts()
  const [selectedReceipt, setSelectedReceipt] = useState(defaultReceipt)
  const [withPayLink, setWithPayLink] = useState(false)
  const shouldEmailReceipt = useAppSelector(selectShouldEmailReceipt)
  const dispatch = useAppDispatch()

  const isTrialing = retailerSettingsManager.get().account_status === 'trialing'

  // Check for payment link availability
  useEffect(() => {
    const checkPaymentLink = async () => {
      const featureFlagEnabled = getFeatureFromCache('lspay_payment_links')
      const isOnAccountSale = status === SaleStatus.ACCOUNT_SALE
      const outstandingBalance = getOutstandingBalance({
        finals: currentSale.finals,
        payments: currentSale.payments,
        payAccountBalance: currentSale.payAccountBalance,
      })
      const isAmountEligible = isAmountEligibleForLSPayLink(outstandingBalance)

      if (
        featureFlagEnabled &&
        isOnAccountSale &&
        isAmountEligible &&
        customer?.email
      ) {
        try {
          const { id: activeOutletId } = registerManager.getActiveOutlet()
          const isEligible = await paymentManager.retrievePaymentLinkAvailability(
            activeOutletId
          )
          setWithPayLink(isEligible)
        } catch (error) {
          setWithPayLink(false)
        }
      } else {
        setWithPayLink(false)
      }
    }

    checkPaymentLink()
  }, [status, currentSale, customer?.email, paymentManager, registerManager])

  const label = useMemo(
    () => getLabel({ status, isSavedService, customer, withPayLink }),
    [customer, isSavedService, status, withPayLink]
  )

  useEffect(() => {
    if (!selectedReceipt && defaultReceipt) {
      setSelectedReceipt(defaultReceipt)
    }
  }, [selectedReceipt, defaultReceipt])

  useEffect(() => {
    if (
      !posStateManager.getTrainingModeEnabled() &&
      !isTrialing &&
      register.email_receipt
    ) {
      dispatch(
        updateReceiptValues({
          receiptEmailAddress: customer?.email || '',
          receiptID: selectedReceipt?.id || '',
        })
      )
    }
  }, [
    customer?.email,
    dispatch,
    isTrialing,
    register.email_receipt,
    selectedReceipt?.id,
  ])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!customer?.email) {
      return
    }

    const checked = e.target.checked
    if (checked) {
      dispatch(
        updateReceiptValues({
          receiptEmailAddress: customer.email,
          receiptID: selectedReceipt?.id || '',
        })
      )
    } else {
      dispatch(
        updateReceiptValues({
          receiptEmailAddress: '',
          receiptID: isQuote(status) ? selectedReceipt?.id || '' : '',
        })
      )
    }
  }

  return (
    <Card className="vd-mt10">
      <div className="vd-ma5 vd-flex">
        <div className="vd-mr4">
          <Switch
            small={true}
            checked={shouldEmailReceipt}
            onChange={handleChange}
          />
        </div>
        <div className="vd-align-left">
          <div className="vd-mb2" data-cy="email-toggle">
            {label}
          </div>
          {isTrialing ? (
            <T
              _str="Once you’ve activated your account, you’ll be able to send email receipts to your customers."
              _context="Message replacing select email receipt for trialling retailers"
            />
          ) : null}
          {otherReceipts.length && selectedReceipt && !isTrialing ? (
            <div>
              <T
                _str="Using {receiptSelect}"
                _context="Select receipt for email"
                receiptSelect={
                  <SelectReceiptButton
                    selected={selectedReceipt}
                    onSelect={setSelectedReceipt}
                  />
                }
              />
            </div>
          ) : null}
          {!otherReceipts.length && selectedReceipt && !isTrialing ? (
            <T
              _str="Using {receiptName}"
              _context="Receipt template for email"
              receiptName={selectedReceipt.name}
            />
          ) : null}
        </div>
      </div>
    </Card>
  )
}
